import { useEffect, useState } from 'react';
import { useThemeCarousel } from '@/contexts/ThemeCarouselContext';
import { THEME_REGISTRY } from '@/themes/index';

const DISMISSED_KEY = 'reporter-carousel-banner-dismissed-at-idx';

const FAMILY_ICON: Record<string, string> = {
  'classic-print': '🗞',
  'digital-native': '📱',
  'immersive-cinematic': '🎬',
  'cultural-regional': '🌍',
  'functional-specialist': '📋',
  'legacy': '📖',
};

function formatCountdown(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }
  return `${seconds}s`;
}

export default function ThemeCarouselIndicator() {
  const { currentIndex, currentThemeId, secondsUntilNext, isPaused, advance } =
    useThemeCarousel();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored !== null && parseInt(stored, 10) === currentIndex;
  });

  // Re-show on theme change
  useEffect(() => {
    const stored = localStorage.getItem(DISMISSED_KEY);
    const storedIdx = stored !== null ? parseInt(stored, 10) : -1;
    if (storedIdx !== currentIndex) {
      setDismissed(false);
    }
  }, [currentIndex]);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, String(currentIndex));
  };

  if (dismissed) return null;

  const meta = THEME_REGISTRY[currentThemeId];
  if (!meta) return null;

  const familyIcon = FAMILY_ICON[meta.family] ?? '◆';
  const progress = isPaused ? 100 : Math.round(((7200 - secondsUntilNext) / 7200) * 100);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`Current theme: ${meta.displayName}. Next theme in ${formatCountdown(secondsUntilNext)}.`}
      className="fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-4 py-2 bg-black/90 backdrop-blur-sm text-white text-xs border-b border-white/10"
      style={{ accentColor: meta.accentColor }}
    >
      {/* Progress ring — desktop */}
      <div className="hidden sm:flex shrink-0 items-center justify-center w-7 h-7" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 28 28" className="-rotate-90">
          <circle cx="14" cy="14" r="11" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" />
          <circle
            cx="14" cy="14" r="11"
            fill="none"
            stroke={meta.accentColor}
            strokeWidth="2.5"
            strokeDasharray={`${2 * Math.PI * 11}`}
            strokeDashoffset={`${2 * Math.PI * 11 * (1 - progress / 100)}`}
            strokeLinecap="round"
            className="transition-all duration-1000"
          />
        </svg>
      </div>

      {/* Theme name and family */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span aria-hidden="true">{familyIcon}</span>
        <span className="font-semibold truncate" style={{ color: meta.accentColor }}>
          {meta.displayName}
        </span>
        <span className="hidden sm:inline opacity-50">·</span>
        <span className="hidden sm:inline opacity-50 truncate">{meta.description.slice(0, 60)}…</span>
      </div>

      {/* Countdown + actions */}
      <div className="flex items-center gap-3 shrink-0">
        {isPaused ? (
          <span className="opacity-50 text-[10px] uppercase tracking-wide">Paused</span>
        ) : (
          <span className="opacity-60 tabular-nums">
            Next in <span className="font-semibold text-white">{formatCountdown(secondsUntilNext)}</span>
          </span>
        )}

        <button
          onClick={advance}
          className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide border border-white/20 hover:border-white/60 transition-colors"
          aria-label="Skip to next theme"
        >
          Skip →
        </button>

        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white/80 transition-colors text-base leading-none"
          aria-label="Dismiss theme indicator"
        >
          ×
        </button>
      </div>
    </div>
  );
}
